import { JSX, useContext, useState, useRef, useEffect } from 'react';
import {
  Text,
  Field,
  LinkField,
  RichText,
  Image,
  ImageField,
  Link,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import { ItemValue } from 'components/types/ListItem';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { classNames } from 'assets/utils/helper';
import ThemeContext from 'components/context/ThemeContext';

declare global {
  interface Document {
    startViewTransition?: (callback: () => void) => ViewTransition;
  }
}

interface ViewTransition {
  finished: Promise<void>;
  ready: Promise<void>;
  updateCallbackDone: Promise<void>;
}

type TabChildItem = ItemValue & {
  fields: {
    CTA?: LinkField;
    Headline?: Field<string>;
    Body?: Field<string>;
    Image?: ImageField;
  };
};

type TabGroupProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    title: Field<string>;
    description: Field<string>;
    children: Array<TabChildItem>;
  };
};

const TabGroup = (props: TabGroupProps): JSX.Element => {
  if (!props.fields || !props.fields.children) {
    return <div></div>;
  }

   const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const theme = useContext(ThemeContext)?.themeState?.theme;

  const [activeID, setActiveID] = useState<string | number>(
    props.fields.children[0]?.id || -1
  );
  const [activeChild, setActiveChild] = useState<TabChildItem>(
    props.fields.children[0]
  );
  const indicatorRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const scrollTabToCenter = (itemId: string) => {
    const container = document.querySelector('.nav-tabs') as HTMLElement;
    const tab = document.querySelector(
      `.nav-link[data-target="${itemId}"]`
    ) as HTMLElement;

    if (!container || !tab) return;

    const scrollToCenter = () => {
      const viewportCenter = window.innerWidth / 2;
      const containerRect = container.getBoundingClientRect();
      const targetCenter = viewportCenter - containerRect.left;
      const offset = tab.offsetLeft - targetCenter + tab.clientWidth / 2;
      container.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
    };

    scrollToCenter();
    setTimeout(scrollToCenter, 100);
  };

  const moveIndicator = (itemId: string, skipTransition = false) => {
    const tab = document.querySelector(
      `.nav-link[data-target="${itemId}"]`
    ) as HTMLElement;
    const indicator = indicatorRef.current;

    if (tab && indicator) {
      if (skipTransition) {
        indicator.style.transition = 'none';
      }

      indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
      indicator.style.width = `${tab.offsetWidth}px`;

      if (skipTransition) {
        setTimeout(() => {
          indicator.style.transition = '';
        }, 10);
      }
    }
  };

  const handleActiveTab = (itemId: string) => {
    const clickedChild = props.fields.children.find(item => item.id === itemId);
    if (!clickedChild || itemId === activeID) return;

    const updateState = () => {
      setActiveChild(clickedChild);
      setActiveID(itemId);
    };

    if (!document.startViewTransition) {
      updateState();
      scrollTabToCenter(itemId);
      return;
    }

    document.startViewTransition(updateState);
    scrollTabToCenter(itemId);
  };

  useEffect(() => {
    if (activeID !== -1) {
      const skipTransition = isFirstRender.current;
      isFirstRender.current = false;
      setTimeout(() => moveIndicator(activeID as string, skipTransition), 50);
    }

    const handleResize = () => {
      if (activeID !== -1) {
        moveIndicator(activeID as string);
        scrollTabToCenter(activeID as string);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeID]);

  const getTabLabel = (item: TabChildItem) => {
    return item.displayName || item.name || 'Tab';
  };

  const renderTabContent = (child: TabChildItem) => {
    return (
      <div className="tab-content-wrapper">
        <div
          className={`tab-two-column ${!child.fields?.Image?.value?.src ? 'no-image' : ''}`}
        >
          {child.fields?.Image?.value && (
            <div className="tab-image-column">
              <Image field={child.fields.Image} className="tab-image" />
            </div>
          )}

          <div className="tab-text-column">
            {child.fields?.Headline?.value && (
              <Text
                tag="h3"
                className="tab-headline"
                field={child.fields.Headline}
              />
            )}

            {child.fields?.Body?.value && (
              <RichText field={child.fields.Body} className="tab-body" />
            )}

            {child.fields?.CTA?.value?.href && (
              <Link
                field={{
                  ...child.fields.CTA,
                  value: {
                    ...child.fields.CTA.value,
                    text: child.fields.CTA.value?.text || 'Learn More',
                  },
                }}
                className="osf-green-cta Tab-cta"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classNames('overhauled', marginSize)} id={`tab-group-${props.rendering?.uid}`}>
      <div className="jss-inner-container">
        <div>
          <Text
            tag="h2"
            className="osf-headline-h2 tab-title"
            field={props.fields.title}
            editable={false}
          />
        </div>

        <div>
          <Text
            tag="p"
            className="osf-body-copy tab-description"
            field={props.fields.description}
            editable={false}
          />
        </div>

        <div className="tab-group osf-tab-group">
          <div className="tab-bar-wrapper">
            <div className="nav-tabs">
              {props.fields.children.map(item => (
                <button
                  className={`nav-link ${item.id === activeID ? 'active' : ''}`}
                  aria-label={getTabLabel(item)}
                  data-target={item.id}
                  key={item.id}
                  onClick={() => handleActiveTab(item.id)}
                >
                  <span className="tab-text">{getTabLabel(item)}</span>
                </button>
              ))}
              <div
                className={classNames(
                  'tab-indicator',
                  theme === 'sacon' || theme === 'sfcon'
                    ? 'bg-primary-dark'
                    : 'gradient-horizontal'
                )}
                ref={indicatorRef}
              />
            </div>
          </div>

          {activeID !== -1 && (
            <div className="nav-content">
              <div className="nav-pane">{renderTabContent(activeChild)}</div>

              <div
                className={classNames(
                  'gradient-line',
                  theme === 'sacon' || theme === 'sfcon'
                    ? 'bg-primary-dark'
                    : 'gradient-horizontal'
                )}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TabGroup;
